import { useEffect, useState } from 'react';
import { useTokens } from '../../shared/theme';
import CupMark from '../../shared/CupMark';

/**
 * loading state — skeleton rows (animated pulse), NOT a spinner. Mirrors the
 * shell's shape (crest + name header, a stat row, a couple of table rows) so the
 * transition to content is calm.
 */
export default function LoadingState() {
  const t = useTokens();
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => { setShowSkeleton(true); }, 250);
    return () => { window.clearTimeout(timer); };
  }, []);

  // Most local branding requests finish inside one frame or two. Keep that
  // short transition visually quiet so the generic skeleton never flashes
  // between the school finder and the branded destination.
  if (!showSkeleton) {
    return (
      <div className="min-h-screen w-full" style={{ backgroundColor: t.page }}>
        <span className="sr-only">Loading school portal</span>
      </div>
    );
  }

  const bar = (w, h = 14, extra = '') => {
    return (
      <div
        className={`rounded-md animate-pulse ${extra}`}
        style={{ backgroundColor: t.hairline, width: w, height: h }}
      />
    );
  };
  return (
    <div
      className="min-h-screen w-full"
      style={{ backgroundColor: t.page, color: t.body, fontFamily: t.fonts.body }}
    >
      <div className="mx-auto w-full max-w-[1120px] px-4 sm:px-6 py-8">
        <div className="mb-8"><CupMark size={20} withWordmark /></div>

        {/* header skeleton */}
        <div className="flex items-center gap-4 mb-8">
          <div
            className="h-14 w-14 rounded-xl animate-pulse"
            style={{ backgroundColor: t.hairline }}
          />
          <div className="flex flex-col gap-2">
            {bar('220px', 26)}
            {bar('64px', 3)}
            {bar('180px', 14)}
          </div>
        </div>

        {/* stat row skeleton */}
        <div className="flex flex-wrap gap-3 mb-6">
          {[0, 1, 2, 3].map((i) => {
            return (
              <div
                key={i}
                className="rounded-md border p-3 animate-pulse"
                style={{ backgroundColor: t.statBg, borderColor: t.border, width: 132, height: 64 }}
              />
            );
          })}
        </div>

        {/* table rows skeleton */}
        <div
          className="rounded-2xl border p-6"
          style={{ backgroundColor: t.surface, borderColor: t.border }}
        >
          {[0, 1, 2, 3, 4].map((i) => {
            return (
              <div key={i} className="flex items-center justify-between py-3">
                {bar('40%', 14)}
                {bar('80px', 14)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
